/**
 * Auth Rate Limiter Utility
 * 
 * Prevents rate limiting from Supabase by tracking auth operations
 * and implementing circuit breaker pattern when rate limits are approached
 */

interface AuthOperation {
  timestamp: number;
  type: 'token_refresh' | 'session_check' | 'auth_change';
}

class AuthRateLimiter {
  private operations: AuthOperation[] = [];
  private readonly maxOperationsPerMinute = 10; // Conservative limit
  private readonly circuitBreakerThreshold = 8; // Start backing off at 8 operations
  private isCircuitBreakerOpen = false;
  private circuitBreakerResetTime = 0;
  private readonly circuitBreakerResetDelay = 60000; // 1 minute
  
  /**
   * Check if an auth operation should be allowed
   */
  canPerformOperation(type: AuthOperation['type']): boolean {
    const now = Date.now();
    
    // Reset circuit breaker if enough time has passed
    if (this.isCircuitBreakerOpen && now > this.circuitBreakerResetTime) {
      console.log('Auth rate limiter: Circuit breaker reset');
      this.isCircuitBreakerOpen = false;
      this.operations = []; // Clear old operations
    }
    
    // If circuit breaker is open, block all operations
    if (this.isCircuitBreakerOpen) {
      console.warn(`Auth rate limiter: Circuit breaker is open, blocking ${type} operation`);
      return false;
    }
    
    // Clean up operations older than 1 minute
    this.operations = this.operations.filter(op => now - op.timestamp < 60000);
    
    // Check if we're approaching rate limits
    const recentOperations = this.operations.length;
    
    // Special handling for token refresh operations
    if (type === 'token_refresh') {
      const recentTokenRefreshes = this.operations.filter(op => 
        op.type === 'token_refresh' && now - op.timestamp < 30000
      ).length;
      
      // Allow max 3 token refreshes per 30 seconds
      if (recentTokenRefreshes >= 3) {
        console.warn('Auth rate limiter: Too many recent token refreshes, blocking');
        this.openCircuitBreaker();
        return false;
      }
    }
    
    // Check overall operation count
    if (recentOperations >= this.maxOperationsPerMinute) {
      console.warn(`Auth rate limiter: Too many operations (${recentOperations}), blocking`);
      this.openCircuitBreaker();
      return false;
    }
    
    // Open circuit breaker if approaching threshold
    if (recentOperations >= this.circuitBreakerThreshold) {
      console.warn(`Auth rate limiter: Approaching rate limit (${recentOperations}), opening circuit breaker`);
      this.openCircuitBreaker();
      return false;
    }
    
    return true;
  }
  
  /**
   * Record an auth operation
   */
  recordOperation(type: AuthOperation['type']): void {
    if (!this.canPerformOperation(type)) {
      return; // Don't record blocked operations
    }
    
    this.operations.push({
      timestamp: Date.now(),
      type
    });
    
    console.debug(`Auth rate limiter: Recorded ${type} operation (total: ${this.operations.length})`);
  }
  
  /**
   * Get current rate limit status
   */
  getStatus() {
    const now = Date.now();
    const recentOperations = this.operations.filter(op => now - op.timestamp < 60000);
    
    return {
      isCircuitBreakerOpen: this.isCircuitBreakerOpen,
      recentOperations: recentOperations.length,
      maxOperations: this.maxOperationsPerMinute,
      timeUntilReset: this.isCircuitBreakerOpen ? Math.max(0, this.circuitBreakerResetTime - now) : 0
    };
  }
  
  /**
   * Force reset the rate limiter (use with caution)
   */
  forceReset(): void {
    console.log('Auth rate limiter: Force reset');
    this.operations = [];
    this.isCircuitBreakerOpen = false;
    this.circuitBreakerResetTime = 0;
  }
  
  private openCircuitBreaker(): void {
    this.isCircuitBreakerOpen = true;
    this.circuitBreakerResetTime = Date.now() + this.circuitBreakerResetDelay;
    console.warn(`Auth rate limiter: Circuit breaker opened, will reset at ${new Date(this.circuitBreakerResetTime).toISOString()}`);
  }
}

// Export singleton instance
export const authRateLimiter = new AuthRateLimiter();

// Export utility functions
export const canPerformAuthOperation = (type: AuthOperation['type']) => 
  authRateLimiter.canPerformOperation(type);

export const recordAuthOperation = (type: AuthOperation['type']) => 
  authRateLimiter.recordOperation(type);

export const getAuthRateLimitStatus = () => authRateLimiter.getStatus();

export const resetAuthRateLimit = () => authRateLimiter.forceReset(); 